/**
 * Policy of the silent `/customer-auth/refresh` call, kept in its own module with
 * ZERO imports from `@angular/*`. The `verify:s20-p1` gate bundles this file and
 * EXECUTES `classifyRefreshOutcome` in Node: a symbol living inside a class that
 * calls `inject()` would need an injection context, and the probe has none.
 */

/**
 * What a refresh attempt actually said. Four values, all of them decided
 * explicitly by every caller — `refresh()` producing a verdict nobody reads is the
 * bug this contract replaces.
 */
export type RefreshOutcome = 'authenticated' | 'rejected' | 'throttled' | 'unavailable';

/**
 * How long the panel stops calling `/refresh` after the backend answered 429.
 *
 * (a) The number comes from the `@Throttle(10, 60)` decorator on the `/refresh`
 *     handler (`backend/src/controllers/customer-auth.controller.ts:122-125`) — the
 *     upper bound of the time after which the oldest slot of that counter is
 *     certainly free again. `ThrottlerModule.forRoot({ ttl: 60, limit: 5 })`
 *     (`backend/src/modules/core.module.ts:71`) is only the default that decorator
 *     overrides; the handler's own window is what this constant tracks.
 * (b) A SHORTER value would let the panel ask again inside the same window and get
 *     throttled a second time — the loop this cooldown exists to break.
 * (c) When the backend changes that window, this constant is the ONLY place on the
 *     front to change.
 */
export const REFRESH_THROTTLE_COOLDOWN_MS = 60_000;

/**
 * HTTP status → verdict, enumerated by code and never by range.
 *
 * `400` and `404` sit in the same range as `401` and deliberately come out as
 * `'unavailable'`: written as `status >= 400 && status < 500 ? 'rejected' : …`,
 * this function would log the customer out on the first validation error. The
 * status is also the ONLY reliable discriminator here — the backend answers 429
 * and 401 with a byte-identical body shape (`{ message, statusCode }`), so nothing
 * may be read out of `message`.
 *
 * `0` is the transport failure Angular reports when no response arrived at all;
 * it falls into `'unavailable'` through the same default as every unlisted code.
 */
export function classifyRefreshOutcome(
  status: number,
): Exclude<RefreshOutcome, 'authenticated'> {
  switch (status) {
    case 401:
    case 403:
      return 'rejected';
    case 429:
      return 'throttled';
    default:
      return 'unavailable';
  }
}
